import { JOURNEY_TESTING_MODAL } from 'modules/workflows/constants/modals';
import { closeModal } from 'modules/workflows/ducks';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'ui/button';
import { Link } from 'ui/links';
import { Modal, ModalFooter, ModalHeader } from 'ui/modal';
import { Tooltip } from 'ui/tooltip';
import {
  JourneyTestingFormProvider,
  JourneyTestingFormSchemaType,
} from './JourneyTestingModalFormProvider';
import { SampleTriggerEvent } from './SampleTriggerEvent';
import {
  FormSection,
  FormSubsection,
  StyledBodyText,
  StyledDivider,
  StyledModalBody,
} from './styles';
import { TestUser } from './TestUser';

import {
  selectEmail,
  selectIsUuidBasedProject,
  selectUserId,
  selectUuidProjectType,
  UuidProjectType,
} from 'core/userContext';
import { isEmpty } from 'lodash';
import { getTestUserConfig } from './utils';

type JourneyTestingModalProps = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

type Props = JourneyTestingModalProps & {
  /** Whether the project is uuid based */
  isUuidBased: boolean;
};

const content = {
  delayTileBehavior: 'Adjust behavior of delay tiles',
  journeyActions: 'Journey actions',
  runJourneyFor: 'Run journey for',
  submitButton: 'Start test',
  title: 'Test run journey',
};

export const JourneyTesting = ({ isOpen, isUuidBased }: Props) => {
  const dispatch = useDispatch();

  const {
    formState: { errors },
    handleSubmit,
  } = useFormContext<JourneyTestingFormSchemaType>();

  const isTestJourneyBlocked = !isEmpty(errors);

  const closeEditor = () => {
    dispatch(closeModal(JOURNEY_TESTING_MODAL));
  };

  const onSubmit = (data: JourneyTestingFormSchemaType) => {
    // biome-ignore lint/suspicious/noConsole: placeholder for development
    console.log('Form submitted with data:', data);

    // closeEditor(); // if successful
  };

  return (
    <Modal id={JOURNEY_TESTING_MODAL} isModalOpen={isOpen} onClose={closeEditor}>
      <ModalHeader>{content.title}</ModalHeader>
      <StyledModalBody>
        <StyledBodyText>
          Add a person (like yourself or a teammate) to the journey to make sure messages,
          integrations, and user profile updates work as you expect for your users.{' '}
          <Link
            href="https://support.iterable.com/hc/en-us/articles/4411905637396-Testing-and-Publishing-Journeys"
            target="_blank"
            rel="noreferrer noopener"
            hasOpenNewTabIcon
          >
            Learn more
          </Link>
        </StyledBodyText>

        <FormSection>
          <FormSubsection>
            <div>{content.runJourneyFor}</div>
            <TestUser isUuidBased={isUuidBased} />
          </FormSubsection>
          <SampleTriggerEvent />
        </FormSection>

        <StyledDivider />
        <div>{content.delayTileBehavior}</div>
        <StyledDivider />
        <div>{content.journeyActions}</div>
      </StyledModalBody>
      <ModalFooter>
        <Tooltip isEnabled={isTestJourneyBlocked} tooltipContent="Content errors" isUsedOnButton>
          <Button autoFocus onClick={handleSubmit(onSubmit)} isDisabled={isTestJourneyBlocked}>
            {content.submitButton}
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
};

export const JourneyTestingModal = ({ isOpen }: JourneyTestingModalProps) => {
  const recipientEmail = useSelector(selectEmail) || '';
  const userId = useSelector(selectUserId) || '';
  const isUuidBased = useSelector(selectIsUuidBasedProject);
  const uuidProjectType = useSelector(selectUuidProjectType) || UuidProjectType.EmailUnique;

  const { testUser, testUserType } = getTestUserConfig(
    recipientEmail,
    userId,
    isUuidBased,
    uuidProjectType,
  );

  return (
    <JourneyTestingFormProvider testUser={testUser} testUserType={testUserType}>
      <JourneyTesting isOpen={isOpen} isUuidBased={isUuidBased} />
    </JourneyTestingFormProvider>
  );
};
